import React, { useMemo, useState } from "react";

// Styles
import { Fulfillment, Common } from "@secondcloset/types";

import { PlusOutlined } from "@ant-design/icons";
import { Button, Modal, Alert, Table, Input } from "antd";
import { startCase } from "lodash-es";

import * as S from "./styles";

import { useCreateShipment } from "hooks/api/fulfillment/shipment";

import Order from "lib/fulfillment/order";

type OrderType = Fulfillment.Order<"V2">;
type OrderItem = Fulfillment.OrderItem<"V2">;
type BaseNote = Common.BaseNote;

interface Props {
  order?: OrderType;
  orderItems?: OrderItem[];
  reFetchOrder: () => unknown;
}

enum TableColumns {
  name = "name",
  sku = "sku",
  quantity = "quantity",
}

interface ShipmentBody {
  version: number;
  order_id: string;
  items: { product_id: string; quantity: number }[];
  service_code: string;
  job_type: string;
  skip_customer_notification: boolean;
  notes_attributes: Partial<BaseNote>[];
}

const CreateInventoryReceivingShipmentButtonV2: React.FC<Props> = ({ order, orderItems, reFetchOrder }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { shipment, createShipment } = useCreateShipment();
  const [notes, setNotes] = useState("");

  const unshippedItems: OrderItem[] = useMemo(() => Order.getUnshippedItemsV2(orderItems) || [], [orderItems]);

  if (!order) return null;

  const createInventoryReceivingShipment = async () => {
    const items = unshippedItems.map((item) => ({
      product_id: item.product.id,
      quantity: item.available_actions.delivery_item_ids?.length || 0,
    }));

    const inventoryReceivingShipmentBody: ShipmentBody = {
      version: 2,
      order_id: order.id,
      items: items,
      service_code: "second_closet_standard",
      job_type: "inventory_warehouse_receiving",
      skip_customer_notification: true,
      notes_attributes: [{ text: notes.trim() }],
    };

    createShipment(inventoryReceivingShipmentBody).then(() => {
      setIsModalVisible(false);
      reFetchOrder();
    });
  };

  const renderError = () => {
    const error = shipment.error;
    if (!error) return null;
    return <Alert message={error} type="error" banner />;
  };

  const getColumns = () => {
    return Object.values(TableColumns).map((column) => {
      return { title: startCase(column), dataIndex: column, key: column };
    });
  };

  const getDataSource = () => {
    return Object.values(unshippedItems).map((item: OrderItem, index) => {
      return {
        key: index,
        name: item.product.name,
        sku: item.product.sku,
        quantity: item.available_actions?.delivery_item_ids?.length || 0,
      };
    });
  };

  const renderToBeReceivedItemsTable = () => {
    const paginationConfig = Object.values(unshippedItems).length > 10 ? {} : false;
    return <Table columns={getColumns()} dataSource={getDataSource()} pagination={paginationConfig}></Table>;
  };
  const renderShipmentNotes = () => {
    return (
      <S.ShipmentNote>
        <S.ShipmentNoteTitle>Shipment Notes:</S.ShipmentNoteTitle>
        <Input.TextArea
          value={notes}
          placeholder="please enter reference/tracking number if possible"
          onChange={(e) => setNotes(e.target.value)}
          allowClear
        />
      </S.ShipmentNote>
    );
  };

  const renderModal = () => {
    return (
      <Modal
        visible={isModalVisible}
        onOk={createInventoryReceivingShipment}
        onCancel={() => setIsModalVisible(false)}
        title="Create Inventory Warehouse Receiving Shipments"
        confirmLoading={shipment.loading}
        destroyOnClose
      >
        {renderError()}
        {renderToBeReceivedItemsTable()}
        {renderShipmentNotes()}
      </Modal>
    );
  };

  return (
    <div>
      <Button icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
        Create Inventory Warehouse Receiving Shipments
      </Button>
      {renderModal()}
    </div>
  );
};

export default CreateInventoryReceivingShipmentButtonV2;
